export const PRACTICE_TRACK_OPTIONS = [
  {
    value: 'SENIOR_ADVOCATE',
    label: 'Senior Advocate',
    shortLabel: 'Senior',
    role: 'ADVOCATE',
    detail: 'Hearing-first command view for openings, bench pressure, and final strategy.',
  },
  {
    value: 'AOR_CHAMBER_LEAD',
    label: 'AOR / Chamber Lead',
    shortLabel: 'Lead',
    role: 'ADVOCATE',
    detail: 'Listings, filing readiness, delegation, and chamber movement in one spine.',
  },
  {
    value: 'JUNIOR_CLERK',
    label: 'Junior / Clerk',
    shortLabel: 'Junior',
    role: 'ADVOCATE',
    detail: 'Bundle execution, chronology, defects, and senior briefing.',
  },
  {
    value: 'INDEPENDENT_LITIGATOR',
    label: 'Independent Litigator',
    shortLabel: 'Independent',
    role: 'ADVOCATE',
    detail: 'Run intake, drafting, hearings, and client follow-through on your own matters.',
  },
  {
    value: 'FIRM_LAWYER',
    label: 'Firm Lawyer',
    shortLabel: 'Firm',
    role: 'ADVOCATE',
    detail: 'Matter continuity, research trails, and review-ready work product.',
  },
  {
    value: 'CORPORATE_COUNSEL',
    label: 'Corporate Counsel',
    shortLabel: 'Counsel',
    role: 'ADVOCATE',
    detail: 'Translate live disputes into legal-risk, advice, and outside-counsel workflows.',
  },
  {
    value: 'STUDENT',
    label: 'Student',
    shortLabel: 'Student',
    role: 'STUDENT',
    detail: 'Case explain, revision notes, and courtroom-ready understanding.',
  },
];

const LEGACY_TRACK_ALIASES = {
  SELF_PRACTITIONER: 'INDEPENDENT_LITIGATOR',
  CHAMBER_JUNIOR: 'JUNIOR_CLERK',
  LAW_FIRM_LAWYER: 'FIRM_LAWYER',
};

function buildChatWorkflow({ workflowId, label, description, prompt, mode, path = '/chat' }) {
  return {
    workflow_id: workflowId,
    label,
    description,
    prompt,
    mode,
    path,
    kind: 'CHAT',
  };
}

export function getPracticeTrackMeta(practiceTrack) {
  const normalized = LEGACY_TRACK_ALIASES[practiceTrack] || practiceTrack;
  return PRACTICE_TRACK_OPTIONS.find((option) => option.value === normalized) || PRACTICE_TRACK_OPTIONS[3];
}

export function buildStarterWorkflows(practiceTrack) {
  const track = getPracticeTrackMeta(practiceTrack).value;
  const common = [
    buildChatWorkflow({
      workflowId: 'hearing_prep',
      label: 'Continue this hearing prep',
      description: 'Open with the facts and urgency. Let Chat shape the hearing note before deeper handoff.',
      prompt: 'I have an upcoming hearing. Read the uploaded papers, isolate the strongest points, and prepare a concise hearing note with likely objections and replies.',
      mode: 'STRATEGY',
    }),
    buildChatWorkflow({
      workflowId: 'draft_from_facts',
      label: 'Finish this draft',
      description: 'Move from client facts, emails, or uploaded papers into a first usable draft.',
      prompt: 'Use the uploaded file and facts to prepare a first draft, list the missing facts, and keep the tone chamber-ready.',
      mode: 'DRAFT',
    }),
    {
      workflow_id: 'voice_capture',
      label: 'Review this accepted Jolly memory',
      description: 'Start with voice, then continue the same matter in chat or research without losing context.',
      prompt: 'Capture a multilingual dictation, hearing debrief, or client call and convert it into structured next steps.',
      mode: 'dictation',
      path: '/jolly',
      kind: 'JOLLY',
    },
  ];

  if (track === 'STUDENT') {
    return [
      buildChatWorkflow({
        workflowId: 'study_from_case',
        label: 'Explain this authority set',
        description: 'Turn a case, doctrine, or uploaded note into revision material without leaving the workflow.',
        prompt: 'Teach me this topic from the uploaded material, extract the governing rule, turn it into revision notes, and flag the most exam-relevant authorities.',
        mode: 'EXAM_PREP',
      }),
      buildChatWorkflow({
        workflowId: 'case_explain',
        label: 'Build a case brief',
        description: 'Get a quick multilingual brief before deciding whether the issue needs deeper research.',
        prompt: 'Explain the uploaded judgment in simple language, then produce a case brief, IRAC, and three points I must remember.',
        mode: 'EXPLAIN',
      }),
      ...common,
    ];
  }

  if (track === 'JUNIOR_CLERK') {
    return [
      buildChatWorkflow({
        workflowId: 'senior_brief',
        label: 'Prepare senior brief',
        description: 'Condense the matter into the short note a senior would want before conference or hearing.',
        prompt: 'Turn the file into a short senior briefing note with facts, issues, likely pushback, exact next action items, and what still needs verification.',
        mode: 'EXPLAIN',
      }),
      ...common,
    ];
  }

  if (track === 'FIRM_LAWYER') {
    return [
      buildChatWorkflow({
        workflowId: 'matter_summary',
        label: 'Resume matter board',
        description: 'Open a matter-aware thread that keeps research, drafting, and continuity aligned across the file.',
        prompt: 'Resume this matter from the current file state, summarize where we are, what still needs authority support, and which output should be finished next.',
        mode: 'RESEARCH',
      }),
      ...common,
    ];
  }

  if (track === 'SENIOR_ADVOCATE') {
    return [
      buildChatWorkflow({
        workflowId: 'hearing_opening',
        label: 'Build morning hearing brief',
        description: 'Lock the opening, objections, and fallback asks for today’s court-facing work.',
        prompt: 'Prepare a morning hearing brief with opening line, strongest point, likely bench questions, fallback ask, and exact record references to carry.',
        mode: 'STRATEGY',
        path: '/hearings',
      }),
      buildChatWorkflow({
        workflowId: 'bench_pressure',
        label: 'Pressure-test bench questions',
        description: 'Surface what the bench is most likely to press first.',
        prompt: 'Stress-test this matter from the bench perspective and list the toughest questions, the clean answer, and the fallback position for each.',
        mode: 'STRATEGY',
      }),
      ...common,
    ];
  }

  if (track === 'AOR_CHAMBER_LEAD') {
    return [
      buildChatWorkflow({
        workflowId: 'listing_command',
        label: 'Run listing and filing board',
        description: 'Start from today’s listings, filing pressure, and the chamber queue instead of a blank chat.',
        prompt: 'Summarize the matters that need movement today, especially listings, filing defects, and chamber follow-ups, and turn them into a clean execution queue.',
        mode: 'STRATEGY',
      }),
      buildChatWorkflow({
        workflowId: 'client_update_pack',
        label: 'Prepare client update pack',
        description: 'Turn live matter posture into a client-safe status update.',
        prompt: 'Prepare a client-safe update with current status, what changed, what we need from the client, and the next expected legal move.',
        mode: 'EXPLAIN',
      }),
      ...common,
    ];
  }

  if (track === 'CORPORATE_COUNSEL') {
    return [
      buildChatWorkflow({
        workflowId: 'risk_update',
        label: 'Prepare risk update',
        description: 'Translate a live dispute into a board-facing legal-risk note.',
        prompt: 'Summarize the dispute posture, legal risk, immediate options, and the cleanest business-safe explanation for internal stakeholders.',
        mode: 'EXPLAIN',
      }),
      buildChatWorkflow({
        workflowId: 'outside_counsel_followup',
        label: 'Review outside counsel work',
        description: 'Check whether the current advice, draft, or hearing lane is actually ready to rely on.',
        prompt: 'Review the current matter posture like in-house counsel, identify missing authority or record support, and list the follow-ups external counsel should close next.',
        mode: 'RESEARCH',
      }),
      ...common,
    ];
  }

  return [
    buildChatWorkflow({
      workflowId: 'matter_intake',
      label: 'Open a live matter',
      description: 'Open with the practical problem and get a clear next move fast.',
      prompt: 'I need help on a live matter. Organize the facts, identify the strongest path, and tell me what should happen next today.',
      mode: 'STRATEGY',
    }),
    ...common,
  ];
}

export function buildStarterPrompt(workflow, starterGoal = '') {
  const base = String(workflow?.prompt || '').trim();
  const goal = String(starterGoal || '').trim();
  if (!goal) return base;
  return `${base}\n\nSpecific goal for this session: ${goal}`;
}

export function buildStarterLaunchPath(workflow, starterGoal = '') {
  const selected = workflow || buildStarterWorkflows('INDEPENDENT_LITIGATOR')[0];
  if (selected.kind === 'JOLLY') return selected.path || '/jolly';
  const params = new URLSearchParams();
  const prompt = buildStarterPrompt(selected, starterGoal);
  if (prompt) params.set('prefill_query', prompt);
  if (selected.mode) params.set('starter_mode', selected.mode);
  params.set('activation', '1');
  const path = selected.path || '/chat';
  return `${path}${params.toString() ? `?${params.toString()}` : ''}`;
}
